/**
 * Voice-note recording + transcription state (#3829).
 *
 * Records with the browser's own MediaRecorder (webm/opus where supported, mp4
 * on Safari/iOS), uploads the clip once for transcription and hands the text to
 * `onTranscript` for the caller to insert at the cursor. Nothing is sent until
 * the user stops recording; nothing is kept afterwards (the clip lives only in
 * memory for the one upload).
 *
 * `available` is false -- and the mic is not drawn -- when the server has voice
 * notes switched off, or the browser cannot record.
 *
 * Hosts that hide rather than unmount (the feedback form, the help panel) MUST
 * call `cancel()` when they close: it drops the mic, any pending permission
 * prompt's stream and any in-flight upload, and nothing is inserted afterwards.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getVoiceStatus, transcribeVoice, voiceErrorMessage } from "../../api/voice";

export type VoicePhase = "idle" | "recording" | "transcribing";

const DEFAULT_MAX_SECONDS = 60;
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

export function browserCanRecord(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

function pickMimeType(): string | undefined {
  const MR = window.MediaRecorder;
  if (typeof MR?.isTypeSupported !== "function") return undefined;
  return MIME_CANDIDATES.find((t) => MR.isTypeSupported(t));
}

function micErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Microphone access is blocked. Allow it in your browser settings to record a voice note.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No microphone was found on this device.";
  }
  return "Couldn't start the microphone. Please try again.";
}

export interface VoiceNote {
  available: boolean;
  phase: VoicePhase;
  /** Whole seconds recorded so far. */
  elapsed: number;
  maxSeconds: number;
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
  /** Abandon everything: release the mic, abort the upload, discard the clip. */
  cancel: () => void;
  clearError: () => void;
}

export function useVoiceNote(onTranscript: (text: string) => void): VoiceNote {
  const [enabled, setEnabled] = useState(false);
  const [maxSeconds, setMaxSeconds] = useState(DEFAULT_MAX_SECONDS);
  const [phase, setPhase] = useState<VoicePhase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  // True while getUserMedia is pending (the permission prompt): a second tap is ignored.
  const startingRef = useRef(false);
  // Bumped by cancel(); any async step from an older session drops its result.
  const sessionRef = useRef(0);

  const canRecord = browserCanRecord();

  useEffect(() => {
    mountedRef.current = true;
    if (!canRecord) return undefined;
    let live = true;
    void getVoiceStatus().then((s) => {
      if (!live) return;
      setEnabled(!!s.enabled);
      if (s.max_seconds && s.max_seconds > 0) setMaxSeconds(s.max_seconds);
    });
    return () => {
      live = false;
    };
  }, [canRecord]);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const releaseMic = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Tear down without touching React state (safe from the unmount cleanup).
  const teardown = useCallback(() => {
    sessionRef.current += 1;
    startingRef.current = false;
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (rec) {
      rec.ondataavailable = null;
      rec.onstop = null;
      if (rec.state !== "inactive") rec.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    chunksRef.current = [];
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    teardown();
    if (!mountedRef.current) return;
    setPhase("idle");
    setElapsed(0);
    setError(null);
  }, [teardown]);

  // Unmount: drop the mic and any in-flight upload.
  useEffect(
    () => () => {
      mountedRef.current = false;
      teardown();
    },
    [teardown],
  );

  const transcribe = useCallback(async (blob: Blob, seconds: number) => {
    const session = sessionRef.current;
    const current = () => mountedRef.current && sessionRef.current === session;
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase("transcribing");
    try {
      const text = await transcribeVoice(blob, seconds, controller.signal);
      if (!current() || controller.signal.aborted) return;
      if (text) onTranscriptRef.current(text);
      else setError("We couldn't hear anything in that recording. Please try again.");
    } catch (err) {
      if (!current() || controller.signal.aborted) return;
      setError(voiceErrorMessage(err));
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (current()) setPhase("idle");
    }
  }, []);

  const stop = useCallback(() => {
    clearTimer();
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  }, []);

  const start = useCallback(async () => {
    if (phase !== "idle" || startingRef.current) return;
    startingRef.current = true;
    const session = sessionRef.current;
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (sessionRef.current !== session) return;
      startingRef.current = false;
      if (mountedRef.current) setError(micErrorMessage(err));
      return;
    }
    // Cancelled (or unmounted) while the permission prompt was up: let go at once.
    if (!mountedRef.current || sessionRef.current !== session) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    startingRef.current = false;
    streamRef.current = stream;
    const mimeType = pickMimeType();
    let rec: MediaRecorder;
    try {
      rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      releaseMic();
      setError("Couldn't start the microphone. Please try again.");
      return;
    }
    recorderRef.current = rec;
    chunksRef.current = [];
    rec.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      clearTimer();
      releaseMic();
      recorderRef.current = null;
      const seconds = (Date.now() - startedAtRef.current) / 1000;
      const type = rec.mimeType || mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (!blob.size) {
        setPhase("idle");
        setError("No audio was recorded. Please try again.");
        return;
      }
      void transcribe(blob, Math.min(seconds, maxSeconds));
    };
    startedAtRef.current = Date.now();
    setElapsed(0);
    rec.start();
    setPhase("recording");
    timerRef.current = window.setInterval(() => {
      const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(secs);
      // Hard stop at the cap so the server never refuses a too-long note.
      if (secs >= maxSeconds) stop();
    }, 250);
  }, [phase, maxSeconds, stop, transcribe]);

  const clearError = useCallback(() => setError(null), []);

  return {
    available: canRecord && enabled,
    phase,
    elapsed,
    maxSeconds,
    error,
    start,
    stop,
    cancel,
    clearError,
  };
}

/** `m:ss` for the recording timer. */
export function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Insert `text` into `value` at the element's caret (or at the end when there is
 * no element / selection), padding with a space where it would run into a word.
 * Returns the new value and the caret position just after the inserted text.
 */
export function insertAtCursor(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  value: string,
  text: string,
  maxLength?: number,
): { value: string; caret: number } {
  const start = el?.selectionStart ?? value.length;
  const end = el?.selectionEnd ?? start;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const lead = before && !/\s$/.test(before) ? " " : "";
  const trail = after && !/^\s/.test(after) ? " " : "";
  let insert = `${lead}${text}${trail}`;
  if (maxLength !== undefined) {
    const room = Math.max(0, maxLength - before.length - after.length);
    insert = insert.slice(0, room);
  }
  return { value: before + insert + after, caret: before.length + insert.length };
}
